<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.registry_oversight.title') }}
            </h2>
            <p class="text-gray-600">
              {{ $t('government.registry_oversight.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Registry Content -->
      <main id="registry-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Filters and Controls -->
        <div
          id="filters-section"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex flex-wrap gap-4 items-center justify-between">
            <div class="flex flex-wrap gap-4">
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.registry_oversight.filters.all_project_types') }}</option>
                <option>{{ $t('government.registry_oversight.filters.renewable_energy') }}</option>
                <option>{{ $t('government.registry_oversight.filters.forestry') }}</option>
                <option>{{ $t('government.registry_oversight.filters.agriculture') }}</option>
                <option>{{ $t('government.registry_oversight.filters.energy_efficiency') }}</option>
              </select>
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.registry_oversight.filters.all_developers') }}</option>
                <option>Green Energy Corp</option>
                <option>Forest Solutions Ltd</option>
                <option>Sustainable Agri</option>
              </select>
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.registry_oversight.filters.all_status') }}</option>
                <option>{{ $t('government.registry_oversight.filters.active') }}</option>
                <option>{{ $t('government.registry_oversight.filters.pending') }}</option>
                <option>{{ $t('government.registry_oversight.filters.suspended') }}</option>
              </select>
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.registry_oversight.filters.all_standards') }}</option>
                <option>{{ $t('government.registry_oversight.filters.vcs') }}</option>
                <option>{{ $t('government.registry_oversight.filters.gold_standard') }}</option>
                <option>{{ $t('government.registry_oversight.filters.cdm') }}</option>
              </select>
            </div>
            <div class="flex gap-2">
              <button
                class="px-4 py-2 bg-forest-green text-white rounded-md text-sm hover:bg-green-700"
              >
                <i class="fa-solid fa-filter mr-2"></i>{{ $t('government.registry_oversight.filters.apply_filters') }}
              </button>
              <button
                class="px-4 py-2 border border-light-gray rounded-md text-sm hover:bg-gray-50"
              >
                <i class="fa-solid fa-download mr-2"></i>{{ $t('government.registry_oversight.filters.export') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Tags Section -->
        <div
          id="tags-section"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <h3 class="text-lg font-medium text-dark-navy mb-4">{{ $t('government.registry_oversight.project_tags.title') }}</h3>
          <div class="flex flex-wrap gap-3">
            <span
              class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-pointer hover:bg-blue-200"
            >
              <i class="fa-solid fa-tag mr-1"></i>{{ $t('government.registry_oversight.project_tags.article6_eligible') }}
            </span>
            <span
              class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm cursor-pointer hover:bg-yellow-200"
            >
              <i class="fa-solid fa-shield-alt mr-1"></i>{{ $t('government.registry_oversight.project_tags.in_buffer') }}
            </span>
            <span
              class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm cursor-pointer hover:bg-orange-200"
            >
              <i class="fa-solid fa-clock mr-1"></i>{{ $t('government.registry_oversight.project_tags.pending_transfer') }}
            </span>
          </div>
        </div>

        <!-- Account Level Ledger -->
        <div
          id="ledger-section"
          class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-dark-navy">
                {{ $t('government.registry_oversight.account_ledger.verified_emissions.title') }}
              </h3>
              <i class="fa-solid fa-check-circle text-forest-green"></i>
            </div>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.verified_emissions.total_verified') }}</span>
                <span class="font-medium">2,450,000 tCO2e</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.verified_emissions.this_month') }}</span>
                <span class="font-medium text-forest-green"
                  >+125,000 tCO2e</span
                >
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-forest-green h-2 rounded-full"
                  style="width: 78%"
                ></div>
              </div>
            </div>
          </div>

          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-dark-navy">
                {{ $t('government.registry_oversight.account_ledger.retired_credits.title') }}
              </h3>
              <i class="fa-solid fa-archive text-electric-blue"></i>
            </div>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.retired_credits.total_retired') }}</span>
                <span class="font-medium">1,890,000 Credits</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.verified_emissions.this_month') }}</span>
                <span class="font-medium text-electric-blue"
                  >+89,000 Credits</span
                >
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-electric-blue h-2 rounded-full"
                  style="width: 65%"
                ></div>
              </div>
            </div>
          </div>

          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-medium text-dark-navy">
                {{ $t('government.registry_oversight.account_ledger.pending_transfer.title') }}
              </h3>
              <i class="fa-solid fa-exchange-alt text-yellow-600"></i>
            </div>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.pending_transfer.awaiting_transfer') }}</span>
                <span class="font-medium">560,000 Credits</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">{{ $t('government.registry_oversight.account_ledger.pending_transfer.processing') }}</span>
                <span class="font-medium text-yellow-600">45,000 Credits</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-yellow-600 h-2 rounded-full"
                  style="width: 23%"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Project Registry Table -->
        <div
          id="projects-table"
          class="bg-white rounded-md border border-light-gray overflow-hidden"
        >
          <div class="p-5 border-b border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy">{{ $t('government.registry_oversight.projects_table.title') }}</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.project_id') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.type') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.developer') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.status') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.credits') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.tags') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.registry_oversight.projects_table.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    PRJ-2024-001
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Solar Farm
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Green Energy Corp
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                      >Active</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    125,000
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >Article 6.2</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    PRJ-2024-002
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Reforestation
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Forest Solutions Ltd
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"
                      >Pending</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    89,500
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full"
                      >In Buffer</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    PRJ-2024-003
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Wind Farm
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    Renewable Power Inc
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                      >Active</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    200,000
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full"
                      >Pending Transfer</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="px-6 py-3 border-t border-light-gray">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-500"
                >{{ $t('government.registry_oversight.pagination.showing') }} 1-3 {{ $t('government.registry_oversight.pagination.of') }} 89 {{ $t('government.registry_oversight.pagination.projects') }}</span
              >
              <div class="flex space-x-2">
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.registry_oversight.pagination.previous') }}
                </button>
                <button
                  class="px-3 py-1 bg-forest-green text-white rounded-md text-sm"
                >
                  1
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  2
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.registry_oversight.pagination.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Ownership History -->
        <div
          id="ownership-section"
          class="bg-white p-5 rounded-md border border-light-gray mt-6"
        >
          <h3 class="text-lg font-medium text-dark-navy mb-4">
            {{ $t('government.registry_oversight.ownership_history.title') }}
          </h3>
          <div class="space-y-4">
            <div
              class="flex items-center justify-between p-4 bg-gray-50 rounded-md"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="w-10 h-10 bg-forest-green rounded-full flex items-center justify-center"
                >
                  <i class="fa-solid fa-exchange-alt text-white"></i>
                </div>
                <div>
                  <p class="font-medium">{{ $t('government.registry_oversight.ownership_history.credit_transfer') }}</p>
                  <p class="text-sm text-gray-600">
                    PRJ-2024-001 → International Registry
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-medium">50,000 Credits</p>
                <p class="text-sm text-gray-600">Dec 15, 2024</p>
              </div>
            </div>
            <div
              class="flex items-center justify-between p-4 bg-gray-50 rounded-md"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="w-10 h-10 bg-electric-blue rounded-full flex items-center justify-center"
                >
                  <i class="fa-solid fa-certificate text-white"></i>
                </div>
                <div>
                  <p class="font-medium">{{ $t('government.registry_oversight.ownership_history.credit_issuance') }}</p>
                  <p class="text-sm text-gray-600">
                    PRJ-2024-002 → National Registry
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-medium">89,500 Credits</p>
                <p class="text-sm text-gray-600">Dec 12, 2024</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
